import React from "react";
import { PropsWithChildren } from "react";

import { ReactComponent as MoreIcon } from "@common/assets/images/details-pane-icons/send-forward-icon.svg";
import Heading from "@common/components/Heading/Heading";
import Tooltip from "@common/components/Tooltip/Tooltip";
import { vars } from "@common/styles";
import * as Styled from "@main/pages/Release/views/MetricCards/ReleaseMetricCard.styles";

export interface ReleaseMetricCardBaseProps {
  style?: React.CSSProperties;
  score?: number | null;
  highlight?: boolean;
  error?: boolean;
  onMoreClick?: () => void;
}

type ReleaseMetricCardProps = PropsWithChildren<{
  style?: React.CSSProperties;
  title: string;
  score?: number | null;
  highlight?: boolean;
  error?: boolean;
  onMoreClick?: () => void;
  helperView?: React.ReactNode;
  icon?: string;
}>;

const ReleaseMetricCard = ({
  style,
  title,
  score,
  children,
  highlight,
  error,
  helperView,
  onMoreClick,
  icon,
}: ReleaseMetricCardProps) => {
  return (
    <Styled.ReleaseMetricCard
      style={style}
      $highlight={highlight}
      $error={error}
      $column
    >
      <Styled.ReleaseMetricHeading style={{ marginBottom: "0.9rem" }}>
        <div style={{ flex: 1, flexDirection: "row", display: "flex", alignItems: "center" }}>
          {icon ? <Styled.ReleaseMetricIcon src={icon} /> : null}
          <Heading
            heading3
            marginBottom={0}
          >
            {title}
          </Heading>
          {score !== undefined ? (
            <Tooltip
              style={{ width: "auto" }}
              text={score !== null ? `${title} score` : `${title} score not available`}
              position={"top"}
            >
              <ReleaseMetricScore
                style={{ marginRight: "0.5rem", marginLeft: "0.8rem" }}
                score={score}
              />
            </Tooltip>
          ) : null}
        </div>
        {onMoreClick ? (
          <Tooltip
            style={{ width: "auto" }}
            text={"See details"}
            position={"top"}
          >
            <Styled.ReleaseMetricMoreButton onClick={onMoreClick}>
              <MoreIcon />
            </Styled.ReleaseMetricMoreButton>
          </Tooltip>
        ) : (
          <div style={{ height: "3.4rem" }} />
        )}
        {helperView ? helperView : null}
      </Styled.ReleaseMetricHeading>
      {children}
    </Styled.ReleaseMetricCard>
  );
};

export { ReleaseMetricCard };

interface ReleaseMetricScoreProps {
  style?: React.CSSProperties;
  className?: string;
  score?: number | null;
}

const ReleaseMetricScore = ({ style, className, score }: ReleaseMetricScoreProps) => {
  return (
    <Styled.ReleaseMetricScore
      className={className}
      style={style}
      $gradient={getScoreGradient(score)}
    >
      <Styled.ReleaseMetricScoreText>
        {score != null ? (score === 10 ? "10" : score.toFixed(1)) : "n/a"}
      </Styled.ReleaseMetricScoreText>
    </Styled.ReleaseMetricScore>
  );
};

export { ReleaseMetricScore };

export const SCORE_GRADIENTS = [
  "linear-gradient(105.09deg, #E72121 17.24%, #B1012D 82.32%)", // 0–1.9 red
  "linear-gradient(105.09deg, #D27620 17.24%, #CD2828 82.32%)", // 2–3.9 orange
  "linear-gradient(105.09deg, #E4D100 17.24%, #C19700 82.32%)", // 4–5.9 yellow
  "linear-gradient(105.09deg, #7ABB00 17.24%, #867F00 82.32%)", // 6–7.9 lime
  "linear-gradient(105.09deg, #3BD094 17.24%, #087600 82.32%)", // 8–10 green
];

const getScoreGradient = (score: number | null | undefined): string => {
  if (!score && score !== 0) return vars.colors.grey80;
  const index = Math.min(Math.floor(score / 2), SCORE_GRADIENTS.length - 1);
  return SCORE_GRADIENTS[index];
};
